import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Link } from "react-router-dom";
import { api, LOGO_URL } from "../lib/api";
import ProductCard from "../components/ProductCard";
import DropCountdown from "../components/DropCountdown";
import { ArrowRight, Sparkles, Zap } from "lucide-react";
import HalloweenBanner from "../theme/halloween/HalloweenBanner";

const HERO_BG = "https://images.pexels.com/photos/12511863/pexels-photo-12511863.jpeg";
const CAT_IMG = {
  flower: "https://images.unsplash.com/photo-1732450210368-852bd7befc46",
  wax: "https://images.pexels.com/photos/35007453/pexels-photo-35007453.jpeg",
  carts: "https://images.unsplash.com/photo-1548239329-2a9e54620045",
  edibles: "https://images.unsplash.com/photo-1760577352009-a744e820bd2c",
};
const LIFESTYLE = "https://images.unsplash.com/photo-1767897213817-8664d9b82393";

// Fallbacks used when API fails or returns empty strings. Keep these in sync
// with backend DEFAULT_HOME_CONTENT so the hero never renders blank.
const HOME_FALLBACK = {
  eyebrow: "MEDIA LAUNCH / DROP 26",
  hero_line1: "Keep it",
  hero_line2: "low",
  hero_line3: "unless it's",
  hero_line4: "HighKey.",
  hero_subtitle:
    "Cinematic cannabis drops — flower, wax, carts & edibles. Photographed like album art, delivered like a release.",
  hero_cta: "Enter the shop",
  marquee_text: "HighKey — Exclusive — Kosher — Quality",
  collections_eyebrow: "/ THE COLLECTIONS",
  collections_title_a: "Pick your",
  collections_title_b: "lane",
  collections_desc: "Four categories. One standard: loud. Hover, scroll, and find your frequency.",
  featured_eyebrow: "/ THIS WEEK",
  featured_title: "Fresh drops",
  manifesto_eyebrow: "/ MANIFESTO",
  manifesto_text:
    "We don't do dispensary aesthetics. We drop records, not SKUs. Each strain has a release date.",
};

// Pick the API value when it's a non-empty string, otherwise the fallback.
const pickText = (apiContent, key) => {
  const v = apiContent?.[key];
  if (typeof v === "string" && v.trim().length > 0) return v;
  return HOME_FALLBACK[key];
};

function marqueeColor(i) {
  if (i === 0) return "text-white";
  if (i % 3 === 1) return "text-[var(--hk-orange)] italic";
  if (i % 3 === 2) return "text-[var(--hk-purple-text)]";
  return "text-white";
}

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [apiContent, setApiContent] = useState(null);
  const heroRef = useRef(null);
  const prefersReducedMotion = useReducedMotion();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mq = window.matchMedia("(max-width: 768px)");
    const update = () => setIsMobile(mq.matches);
    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  // Disable expensive parallax on mobile / reduced-motion — keeps phones smooth.
  const enableParallax = !isMobile && !prefersReducedMotion;
  const heroY = useTransform(scrollYProgress, [0, 1], enableParallax ? [0, 300] : [0, 0]);
  const heroScale = useTransform(scrollYProgress, [0, 1], enableParallax ? [1, 1.15] : [1, 1]);
  const heroOpacity = useTransform(scrollYProgress, [0, 0.8], enableParallax ? [1, 0] : [1, 1]);
  const textY = useTransform(scrollYProgress, [0, 1], enableParallax ? [0, -120] : [0, 0]);
  const logoY = useTransform(scrollYProgress, [0, 1], enableParallax ? [0, -200] : [0, 0]);

  useEffect(() => {
    api.get("/fresh-drops", { params: { limit: 4 } })
      .then((r) => setFeatured(r.data || []))
      .catch(() => setFeatured([]));
    api.get("/home/content")
      .then((r) => setApiContent(r.data || {}))
      .catch(() => setApiContent({})); // fall back to defaults — never block render
  }, []);

  // Render with fallbacks immediately; replace as the API hydrates. This keeps
  // the hero text visible even if /home/content is slow or fails on production.
  const c = useMemo(() => ({
    eyebrow: pickText(apiContent, "eyebrow"),
    hero_line1: pickText(apiContent, "hero_line1"),
    hero_line2: pickText(apiContent, "hero_line2"),
    hero_line3: pickText(apiContent, "hero_line3"),
    hero_line4: pickText(apiContent, "hero_line4"),
    hero_subtitle: pickText(apiContent, "hero_subtitle"),
    hero_cta: pickText(apiContent, "hero_cta"),
    marquee_text: pickText(apiContent, "marquee_text"),
    collections_eyebrow: pickText(apiContent, "collections_eyebrow"),
    collections_title_a: pickText(apiContent, "collections_title_a"),
    collections_title_b: pickText(apiContent, "collections_title_b"),
    collections_desc: pickText(apiContent, "collections_desc"),
    featured_eyebrow: pickText(apiContent, "featured_eyebrow"),
    featured_title: pickText(apiContent, "featured_title"),
    manifesto_eyebrow: pickText(apiContent, "manifesto_eyebrow"),
    manifesto_text: pickText(apiContent, "manifesto_text"),
  }), [apiContent]);

  // Marquee: split on " — " or just repeat the whole phrase
  const marqueeParts = c.marquee_text.split(/\s+[—–-]\s+/).filter(Boolean);
  const safeMarquee = marqueeParts.length > 0 ? marqueeParts : ["HighKey", "Exclusive", "Kosher", "Quality"];

  return (
    <div className="pt-24 md:pt-28" data-testid="home-page">
      <HalloweenBanner />
      {/* HERO */}
      <section ref={heroRef} className="relative h-[100svh] min-h-[640px] overflow-hidden">
        <motion.div style={{ y: heroY, scale: heroScale, opacity: heroOpacity }} className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/55 to-black" />
        </motion.div>

        <motion.div style={{ y: textY }} className="relative z-10 h-full flex flex-col justify-between px-6 md:px-12 lg:px-24 py-12 md:py-20">
          <div className="flex items-center gap-3" data-testid="hero-eyebrow">
            <Sparkles size={14} className="text-[var(--hk-orange)]" />
            <span className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)]">
              {c.eyebrow}
            </span>
          </div>
          <div>
            <motion.h1
              initial={{ y: 40, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 1, ease: [0.2, 0.8, 0.2, 1] }}
              data-testid="hero-headline"
              className="font-display font-bold uppercase tracking-tighter leading-[0.85] text-white text-[clamp(2.6rem,9vw,8.5rem)] max-w-5xl"
            >
              <span className="block">{c.hero_line1}</span>
              <span className="block italic text-[var(--hk-purple-text)]">{c.hero_line2}</span>
              <span className="block">{c.hero_line3}</span>
              <span className="block text-[var(--hk-orange)]">{c.hero_line4}</span>
            </motion.h1>
            <motion.p
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.3, duration: 1 }}
              data-testid="hero-subtitle"
              className="mt-6 md:mt-8 max-w-xl text-gray-300 text-sm md:text-lg"
            >
              {c.hero_subtitle}
            </motion.p>
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="mt-8 md:mt-10 flex items-center gap-4 flex-wrap"
            >
              <Link
                to="/shop/flower"
                data-testid="hero-shop-btn"
                className="bg-[var(--hk-orange)] text-black px-6 py-4 font-display font-bold uppercase tracking-widest text-sm rounded-sm hover:bg-[var(--hk-orange-hover)] transition-colors glow-orange inline-flex items-center gap-2"
              >
                {c.hero_cta} <ArrowRight size={16} />
              </Link>
              <a
                href="#drop"
                className="text-white/80 hover:text-white font-display uppercase text-sm tracking-widest"
              >
                See the drop ↓
              </a>
            </motion.div>
          </div>
        </motion.div>

        <motion.img
          src={LOGO_URL}
          alt=""
          initial={{ opacity: 0, scale: 0.6, rotate: -10 }}
          animate={{ opacity: 1, scale: 1, rotate: -8 }}
          transition={{ delay: 0.2, duration: 1.2 }}
          style={{ y: logoY }}
          data-testid="hero-floating-logo"
          className="absolute top-32 right-14 sm:top-32 sm:right-12 lg:top-[18%] lg:right-[2%] xl:right-[4%] w-[150px] sm:w-[200px] md:w-[240px] lg:w-[380px] xl:w-[460px] drop-shadow-[0_0_30px_rgba(147,51,234,0.6)] lg:drop-shadow-[0_0_80px_rgba(147,51,234,0.7)] z-20 pointer-events-none"
        />
      </section>

      {/* MARQUEE */}
      <div className="relative overflow-hidden border-y border-white/10 bg-black py-5">
        <div className="flex gap-12 marquee-track whitespace-nowrap font-display text-5xl md:text-7xl uppercase tracking-tight">
          {Array.from({ length: 3 }).map((_, idx) => (
            <React.Fragment key={idx}>
              {safeMarquee.map((part, i) => (
                <span
                  key={`${idx}-${i}`}
                  className={marqueeColor(i)}
                >
                  {i === 0 ? part : `— ${part} —`}
                </span>
              ))}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* DROP COUNTDOWN + NEWSLETTER */}
      <DropCountdown />

      {/* CATEGORY TETRIS */}
      <section id="drop" className="px-6 md:px-12 lg:px-24 py-20 md:py-40">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12 md:mb-16">
          <div>
            <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-purple-text)] mb-4">
              {c.collections_eyebrow}
            </p>
            <h2 className="font-display text-5xl md:text-7xl font-bold uppercase tracking-tighter leading-[0.9]">
              {c.collections_title_a} <span className="italic text-[var(--hk-orange)]">{c.collections_title_b}</span>
            </h2>
          </div>
          <p className="text-gray-400 max-w-xs text-sm">
            {c.collections_desc}
          </p>
        </div>

        <div className="grid grid-cols-12 gap-3 md:gap-6">
          <CategoryTile to="/shop/flower" label="Flower" img={CAT_IMG.flower} cls="col-span-12 md:col-span-7 row-span-2 aspect-[4/3] md:aspect-auto md:h-[560px]" tall />
          <CategoryTile to="/shop/wax" label="Wax" img={CAT_IMG.wax} cls="col-span-12 md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[270px]" />
          <CategoryTile to="/shop/carts" label="Carts" img={CAT_IMG.carts} cls="col-span-6 md:col-span-3 aspect-square md:aspect-auto md:h-[270px]" />
          <CategoryTile to="/shop/edibles" label="Edibles" img={CAT_IMG.edibles} cls="col-span-6 md:col-span-2 aspect-square md:aspect-auto md:h-[270px]" />
        </div>
      </section>

      {/* FEATURED DROPS */}
      <section className="px-6 md:px-12 lg:px-24 py-16 md:py-20 bg-[var(--hk-surface)]">
        <div className="flex items-end justify-between gap-6 mb-10 md:mb-12">
          <div>
            <p className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)] mb-4">
              {c.featured_eyebrow}
            </p>
            <h2 className="font-display text-4xl md:text-6xl font-bold uppercase tracking-tighter">
              {c.featured_title}
            </h2>
          </div>
          <Link
            to="/shop/flower"
            data-testid="see-all-btn"
            className="hidden md:inline-flex items-center gap-2 text-white/80 hover:text-[var(--hk-orange)] font-display uppercase text-xs tracking-widest"
          >
            See everything <ArrowRight size={14} />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-6">
          {featured.slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} index={i} />
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section className="relative px-6 md:px-12 lg:px-24 py-24 md:py-48 overflow-hidden">
        <img src={LIFESTYLE} alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" loading="lazy" />
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/70 to-black" />
        <motion.div
          initial={{ y: 40, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative z-10 max-w-4xl"
        >
          <div className="flex items-center gap-3 mb-6">
            <Zap size={16} className="text-[var(--hk-orange)]" />
            <span className="font-mono-alt text-xs tracking-[0.4em] text-[var(--hk-orange)]">
              {c.manifesto_eyebrow}
            </span>
          </div>
          <p className="font-display text-3xl md:text-5xl lg:text-6xl font-bold uppercase tracking-tight leading-[1.05] text-white">
            {c.manifesto_text}
          </p>
          <Link
            to="/about"
            className="mt-10 inline-flex items-center gap-2 border border-white/20 hover:border-white/60 px-6 py-3 font-display uppercase text-xs tracking-widest"
          >
            Read the manifesto <ArrowRight size={14} />
          </Link>
        </motion.div>
      </section>
    </div>
  );
}

function CategoryTile({ to, label, img, cls, tall }) {
  return (
    <Link
      to={to}
      data-testid={`home-cat-${label.toLowerCase()}`}
      className={`group relative overflow-hidden border border-white/10 hover:border-[var(--hk-purple)]/60 transition-all ${cls}`}
    >
      <img src={img} alt={label} loading="lazy" className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-90 group-hover:scale-105 transition-all duration-700" />
      <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 p-5 md:p-8">
        <span className="font-mono-alt text-[10px] tracking-[0.4em] text-[var(--hk-orange)]">
          / SHOP
        </span>
        <h3 className={`font-display font-bold uppercase tracking-tighter text-white mt-1 ${tall ? "text-5xl md:text-8xl" : "text-3xl md:text-5xl"}`}>
          {label}
        </h3>
        <div className="mt-3 inline-flex items-center gap-2 text-xs text-white/70 group-hover:text-[var(--hk-orange)] uppercase tracking-widest font-display">
          Enter <ArrowRight size={12} />
        </div>
      </div>
    </Link>
  );
}
